import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  buildOnboardingSteps,
  checklistProgress,
  isChecklistDismissed,
  dismissChecklist,
} from '../utils/onboardingChecklist';

// Etykiety per krok — kolejność w tablicy poniżej (utils/onboardingChecklist.js) niesie
// realną kolejność zależności, tu tylko mapujemy klucz -> klucze tłumaczeń.
const STEP_LABELS = {
  companyData: { title: 'onboarding.step.companyData', cta: 'onboarding.step.companyData.cta' },
  inventoryFields: { title: 'onboarding.step.inventoryFields', cta: 'onboarding.step.inventoryFields.cta' },
  equipment: { title: 'onboarding.step.equipment', cta: 'onboarding.step.equipment.cta', secondaryCta: 'onboarding.step.equipment.importCta' },
  client: { title: 'onboarding.step.client', cta: 'onboarding.step.client.cta', secondaryCta: 'onboarding.step.client.importCta' },
  reservation: { title: 'onboarding.step.reservation', cta: 'onboarding.step.reservation.cta' },
};

const Wrapper = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  padding: 24px 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);

  @media (max-width: 768px) {
    padding: 18px 20px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;
`;

const TitleBlock = styled.div``;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: var(--fs-lg);
  font-weight: 700;
  color: var(--text);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const Progress = styled.span`
  flex-shrink: 0;
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: var(--fs-xs);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;

  &:hover {
    color: var(--text);
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 16px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepItem = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--r-md);
  background: ${(p) => (p.$done ? 'transparent' : 'var(--panel-bg)')};
  opacity: ${(p) => (p.$done ? 0.6 : 1)};
`;

const StepCheck = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xs);
  font-weight: 700;
  color: #fff;
  background: ${(p) => (p.$done ? 'var(--success)' : 'var(--input-border)')};
`;

const StepText = styled.span`
  flex: 1;
  min-width: 160px;
  font-size: var(--fs-base);
  font-weight: 500;
  color: var(--text);
  text-decoration: ${(p) => (p.$done ? 'line-through' : 'none')};
`;

const StepActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StepButton = styled.button`
  padding: 6px 14px;
  border-radius: var(--r-sm);
  border: none;
  background: var(--primary);
  color: #fff;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const StepLinkButton = styled.button`
  padding: 6px 14px;
  border-radius: var(--r-sm);
  border: 1px solid var(--input-border);
  background: transparent;
  color: var(--text);
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--panel-bg);
  }
`;

export default function OnboardingChecklist({ isLoading, equipmentCount, clientsCount, reservationsCount, contractSettings, inventoryConfigured }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [dismissed, setDismissed] = useState(() => isChecklistDismissed(userId));

  const isDemo = localStorage.getItem('isDemo') === '1';
  // /import jest admin-gated na backendzie (routes/import.js) — bez tego non-admin klikający
  // skrót importu dostaje mylące 403 zamiast po prostu nie widzieć przycisku, niespójnie z
  // identycznymi przyciskami importu w pustych stanach inventory/index.js i clients.js.
  const isAdmin = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';

  const steps = buildOnboardingSteps({ equipmentCount, clientsCount, reservationsCount, contractSettings, inventoryConfigured });
  const { done, total, complete } = checklistProgress(steps);

  // isLoading===undefined (caller nie przekazał propa) traktujemy jak "nie czekaj" —
  // tylko home.js zna cykl swojego Promise.allSettled, więc gate jest opt-in dla wołającego.
  if (isLoading || dismissed || isDemo || complete) return null;

  const handleDismiss = () => {
    dismissChecklist(userId);
    setDismissed(true);
  };

  return (
    <Wrapper data-cy="onboarding-checklist">
      <HeaderRow>
        <TitleBlock>
          <Title>{t('onboarding.title')}</Title>
          <Subtitle>{t('onboarding.subtitle')}</Subtitle>
        </TitleBlock>
        <HeaderMeta>
          <Progress>
            {t('onboarding.progress').replace('{done}', String(done)).replace('{total}', String(total))}
          </Progress>
          <DismissButton type="button" onClick={handleDismiss} data-cy="onboarding-dismiss">
            {t('onboarding.dismiss')}
          </DismissButton>
        </HeaderMeta>
      </HeaderRow>

      <StepList>
        {steps.map((step) => {
          const labels = STEP_LABELS[step.key];
          return (
            <StepItem key={step.key} data-cy={`onboarding-step-${step.key}`} $done={step.done}>
              <StepCheck $done={step.done}>{step.done ? '✓' : ''}</StepCheck>
              <StepText $done={step.done}>{t(labels.title)}</StepText>
              {!step.done && (
                <StepActions>
                  <StepButton type="button" onClick={() => navigate(step.href)}>
                    {t(labels.cta)}
                  </StepButton>
                  {step.secondaryHref && isAdmin && (
                    <StepLinkButton type="button" onClick={() => navigate(step.secondaryHref)}>
                      {t(labels.secondaryCta)}
                    </StepLinkButton>
                  )}
                </StepActions>
              )}
            </StepItem>
          );
        })}
      </StepList>
    </Wrapper>
  );
}
